/**
 * Post-processing stack. Owned by `look`.
 *
 * Deliberately small: a threshold bloom on emissives, a colour grade, and a vignette.
 * Four extra draw calls and three shader programs, total.
 *
 * WHAT IS NOT HERE, ON PURPOSE
 * ----------------------------
 * No chromatic aberration, no film grain, no lens dirt. Every one of them costs readability
 * at 90 km/h, and readability is the job: the player has to find the next pothole in under
 * 0.4 s. When a critic says the frame needs "more atmosphere", the answer is fog and
 * lighting, not lens artefacts.
 *
 * Motion blur WAS on that list, and it came off in wave 8 — see tuning.js
 * `post.motionBlur` for the argument. The short version: the readability objection is
 * against a UNIFORM screen blur and is still correct about one. The operator implemented
 * here is the analytic screen-space velocity of a static world point under a translating
 * camera, which is identically zero at the focus of expansion, so the thing you are
 * driving at stays sharp and only the periphery streaks. It rides inside the FXAA blit:
 * zero extra passes, targets, draw calls or shader programs.
 *
 * PIPELINE
 * --------
 *   scene ──► rtScene (HalfFloat, MSAA, LINEAR — no tonemapping applied yet)
 *                │
 *                ├─► bright-pass + ¼ downsample ──► blur H ──► blur V  (×2 iterations)
 *                │                                                  │
 *                └──────────────────────────────► composite ◄───────┘
 *                                                     │
 *                     exposure → PBR-Neutral tonemap → sRGB encode →
 *                     saturation → S-curve contrast → lift → tint → vignette → canvas
 *
 * Bloom is thresholded in LINEAR HDR before tonemapping, which is the only place it means
 * anything: after tonemapping everything is clamped to 1 and a neon sign and a white wall
 * look identical.
 *
 * The grade runs AFTER the sRGB encode, in perceptual space, because that is where
 * "saturation" and "contrast" behave the way an art director expects. Doing an S-curve in
 * linear space crushes the shadows and murders the mid-tones.
 *
 * Everything is skippable: `ctx.config.quality.post === false` bypasses this entirely and
 * `look` renders straight to the canvas with the same tonemapping curve on the renderer, so
 * the two paths agree to a couple of LSBs.
 */

import * as THREE from 'three';
import { LOOK_TUNING as L } from './tuning.js';

/* ── shared GLSL ──────────────────────────────────────────────────────────────────── */

const VERT = /* glsl */`
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** Khronos PBR Neutral. Hue-preserving, near-identity below 0.8 — see tuning.js. */
const TONEMAP_GLSL = /* glsl */`
vec3 pbrNeutral(vec3 color) {
  const float startCompression = 0.8 - 0.04;
  const float desaturation = 0.15;
  float x = min(color.r, min(color.g, color.b));
  float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;
  color -= offset;
  float peak = max(color.r, max(color.g, color.b));
  if (peak < startCompression) return color;
  float d = 1.0 - startCompression;
  float newPeak = 1.0 - d * d / (peak + d - startCompression);
  color *= newPeak / peak;
  float g = 1.0 - 1.0 / (desaturation * (peak - newPeak) + 1.0);
  return mix(color, vec3(newPeak), g);
}
`;

const SRGB_GLSL = /* glsl */`
vec3 linearToSRGB(vec3 c) {
  c = max(c, vec3(0.0));
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c));
}
`;

const BRIGHT_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform sampler2D tScene;
uniform vec2 uTexel;
uniform float uThreshold;
uniform float uSoftKnee;
uniform float uExposure;

vec3 sampleScene(vec2 uv) { return texture2D(tScene, uv).rgb; }

void main() {
  // 4-tap box while downscaling — cheaper than a proper tent and visually identical at ¼.
  vec3 c = sampleScene(vUv + uTexel * vec2(-1.0, -1.0));
  c += sampleScene(vUv + uTexel * vec2( 1.0, -1.0));
  c += sampleScene(vUv + uTexel * vec2(-1.0,  1.0));
  c += sampleScene(vUv + uTexel * vec2( 1.0,  1.0));
  c *= 0.25 * uExposure;

  float lum = max(c.r, max(c.g, c.b));
  float knee = uThreshold * uSoftKnee + 1e-5;
  float soft = clamp(lum - uThreshold + knee, 0.0, 2.0 * knee);
  soft = soft * soft / (4.0 * knee);
  float contrib = max(soft, lum - uThreshold) / max(lum, 1e-4);
  gl_FragColor = vec4(c * contrib, 1.0);
}
`;

const BLUR_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform sampler2D tSrc;
uniform vec2 uDir;          // (texelX, 0) or (0, texelY), pre-scaled by radius

void main() {
  // 9-tap gaussian, weights from a normalised sigma≈2 kernel.
  vec3 c = texture2D(tSrc, vUv).rgb * 0.2270270270;
  c += texture2D(tSrc, vUv + uDir * 1.3846153846).rgb * 0.3162162162;
  c += texture2D(tSrc, vUv - uDir * 1.3846153846).rgb * 0.3162162162;
  c += texture2D(tSrc, vUv + uDir * 3.2307692308).rgb * 0.0702702703;
  c += texture2D(tSrc, vUv - uDir * 3.2307692308).rgb * 0.0702702703;
  gl_FragColor = vec4(c, 1.0);
}
`;

/**
 * ══ THE WET-ROAD REFLECTION PASS ═══════════════════════════════════════════════════════
 *
 * Its own pass, at half resolution, and it runs ONLY when the weather is wet. See
 * tuning.js `post.wetStreakDownscale` for the full argument; the short version is that the
 * march has to know where the ground is in WORLD space before it can follow the camber,
 * break at the gutter and stop at a crosswalk, and doing it in the composite at full res
 * left no tap budget and stippled the whole carriageway.
 *
 * Writes a pre-gated reflection colour. The composite normalises it against the receiver it
 * actually lands on (which it can only do at full res) and blurs it laterally on the way in.
 */
const STREAK_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;

uniform sampler2D tScene;
uniform sampler2D tBloom;
uniform sampler2D tDepth;
uniform mat4  uInvViewProj;
uniform vec3  uCamPos;
uniform vec3  uCamRight;
uniform float uGroundY;
uniform float uStreakFar;
uniform float uHorizonV;
uniform float uStreakLen;
uniform float uStreakSrcMin;
uniform float uBreakDepth;
uniform float uBreakScale;
uniform float uCamber;
uniform float uChroma;
uniform vec2  uTexel;

float lum709(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

vec3 unproj(vec2 uv, float d) {
  vec4 clip = vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  vec4 w = uInvViewProj * clip;
  return w.xyz / w.w;
}

/** Deterministic hash — integer-free, identical on every GPU we care about (§7.3). */
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
             mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x), u.y);
}

void main() {
  float d = texture2D(tDepth, vUv).x;
  if (d > 0.99995 || vUv.y > uHorizonV) { gl_FragColor = vec4(0.0); return; }

  vec3 P = unproj(vUv, d);

  // Ground only, near enough for a reflection to resolve, below the horizon.
  float lowLying = 1.0 - smoothstep(uGroundY + 0.30, uGroundY + 1.05, P.y);
  float closeBy  = 1.0 - smoothstep(uStreakFar * 0.45, uStreakFar, distance(P, uCamPos));
  float gate = lowLying * closeBy * smoothstep(uHorizonV, uHorizonV - 0.16, vUv.y);
  if (gate < 0.002) { gl_FragColor = vec4(0.0); return; }

  // ── the surface we are lying on ──────────────────────────────────────────────────
  // Two extra depth taps give the real normal, and the normal is what carries the camber.
  // A tap that fell across a silhouette produces a wild normal, so blend back to flat when
  // the neighbourhood is not locally continuous.
  vec2 ox = vec2(uTexel.x, 0.0), oy = vec2(0.0, uTexel.y);
  vec3 Px = unproj(vUv + ox, texture2D(tDepth, vUv + ox).x);
  vec3 Py = unproj(vUv + oy, texture2D(tDepth, vUv + oy).x);
  vec3 n = cross(Py - P, Px - P);
  float nl = length(n);
  n = nl > 1e-6 ? n / nl : vec3(0.0, 1.0, 0.0);
  if (n.y < 0.0) n = -n;
  float sane = 1.0 - smoothstep(0.45, 1.30, length(Px - P) + length(Py - P));
  n = normalize(mix(vec3(0.0, 1.0, 0.0), n, sane));

  // BREAK WHERE THE SURFACE BREAKS. Past ~25 degrees of tilt — the gutter throat, the kerb
  // face, the lip of a crater — the mirror is not pointing along the view axis at all.
  gate *= 1.0 - smoothstep(0.10, 0.40, 1.0 - n.y);

  // BREAK IN WORLD SPACE, NOT SCREEN SPACE. Ruts and standing water are nailed to the road,
  // so the mask has to be a function of P, not of gl_FragCoord: that is what makes it
  // foreshorten toward the vanishing point and stop swimming when the camera moves.
  float bn = vnoise(P.xz * uBreakScale) * 0.62 + vnoise(P.xz * uBreakScale * 3.7) * 0.38;
  gate *= mix(1.0 - uBreakDepth, 1.0, smoothstep(0.28, 0.70, bn));
  if (gate < 0.002) { gl_FragColor = vec4(0.0); return; }

  // How far the surface tilts ACROSS the view — the camber lean, in UV per unit of march.
  float lean = dot(n, uCamRight) * uCamber;

  // 4-phase ordered dither over a 2x2 tile. A pure function of the pixel, so shots stay
  // bit-reproducible (§7.3); at half resolution the composite's bilinear fetch averages the
  // four phases back together, which is what finally kills round 10's comb stipple.
  vec2 px = floor(gl_FragCoord.xy);
  float phase = fract(px.x * 0.125 + px.y * 0.375);

  /* W12 — THE COMB IS NOT GONE, and this loop is why.
     The critic autocorrelated a luma column over a BUILDING-WINDOW reflection and found
     maxima at lag 6, 11-12 and 17: a fixed 5.7-6.0 px vertical repeat sustained over 140 px,
     roughly 23 verbatim copies, in at least five separate places. That is exactly this
     march's step. 12 taps across 0.62 x uStreakLen puts the samples ~6 px apart at 1080p, so
     a small bright emitter is COPIED once per tap down the column rather than smeared into
     one. Round 9 measured a tail-light column at 9.13:1 and called the comb fixed; both
     readings are true, because a tail light is large enough to overlap its own neighbours
     and a window pane is not.
     The pixel phase alone could never fix it: it shifts the whole comb, identically for all
     12 taps of a pixel, so the pattern translates instead of dissolving. What dissolves it is
     a per-TAP longitudinal offset of up to half a step, which makes the copies land on
     different points for every i and every pixel; the half-res bilinear upsample in the
     composite then averages them into a continuum. Same tap count, same cost. */
  vec3 acc = vec3(0.0);
  float wsum = 0.0;
  const float STEP12 = 0.62 / 12.0;
  for (int i = 0; i < 12; i++) {
    float fi = float(i);
    // Golden-ratio decorrelation of tap index against pixel phase, mapped to +-half a step.
    float jt = (fract(fi * 0.754877 + phase * 2.618034) - 0.5) * STEP12;
    float t = max(0.0, (fi + phase) * STEP12 + jt);
    float w = exp(-t * 3.4);
    // Lateral jitter GROWS along the march. A reflection is a mirror image blurred by the
    // water's surface roughness, and that blur accumulates with the path length — so the
    // head of the streak is tight and the tail spreads, which is what the photographs show.
    float jitter = (fract(fi * 0.618034 + phase) - 0.5) * uTexel.x * (1.0 + t * 4.0);
    vec3 s = texture2D(tScene, vUv + vec2(lean * t + jitter, t * uStreakLen)).rgb;
    float emit = smoothstep(uStreakSrcMin * 0.55, uStreakSrcMin * 1.6, max(s.r, max(s.g, s.b)));
    acc += s * emit * w;
    wsum += w;
  }
  const float STEP6 = 0.60 / 6.0;
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float jt = (fract(fi * 0.409052 + phase * 1.324718) - 0.5) * STEP6;
    float t = 0.45 + max(0.0, (fi + phase) * STEP6 + jt);
    float w = exp(-t * 2.0) * 0.42;
    acc += texture2D(tBloom, vUv + vec2(lean * t, t * uStreakLen)).rgb * w;
    wsum += w;
  }

  vec3 st = acc / max(wsum, 1e-4);
  // COLOUR SEPARATION. A march is an average, and an average of a red tail light, an amber
  // lamp and a green sign is amber. Pushing chroma about the streak's own luminance is what
  // gives each emitter its own column instead of one sepia smear (see tuning wetStreakChroma).
  float sl = lum709(st);
  st = max(vec3(0.0), mix(vec3(sl), st, uChroma));

  gl_FragColor = vec4(st * gate, 1.0);
}
`;

const COMPOSITE_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;

uniform sampler2D tScene;
uniform sampler2D tBloom;
uniform sampler2D tStreak;
uniform vec2  uStreakTexel;
uniform float uBloomStrength;
uniform float uExposure;
uniform float uSaturation;
uniform float uContrast;
uniform float uContrastPivot;
uniform vec3  uShadowFloor;
uniform vec3  uTint;
uniform float uVignette;
uniform float uVignetteRadius;
uniform float uVignetteSoftness;
uniform float uAspect;

uniform float uStreak;        // 0 = dry. Overall strength of the wet-road reflection.
uniform float uStreakGate;    // receivers brighter than this take no reflection
uniform float uStreakRatio;   // target ADDED brightness, as a multiple of the receiver
uniform float uStreakCompress;// 0 = raw source variation, 1 = every reflection identical
uniform sampler2D tDepth;     // scene depth — sky occlusion and aerial perspective read it

// ── aerial perspective (see tuning.js fog.aerialNear) ──────────────────────────────
uniform mat4  uInvViewProj;
uniform vec3  uCamPos;
uniform vec3  uAerial;        // (near m, far m, level) — level 0 disables the whole term
uniform vec3  uSkyHorizon;
uniform vec3  uSkyMid;
uniform vec3  uSkyZenith;
uniform vec3  uSkyHaze;
uniform vec3  uSkyK;          // (gradientPower, horizonSoft, gain)

uniform float uAoStrength;    // 0 = off
uniform vec2  uAoRadius;      // (near, far) sample radius in METRES
uniform vec4  uAoK;           // (gain, power, minRadiusUV, maxRadiusUV)
uniform float uAoLightGuard;
uniform vec2  uAoProj;        // (0.5/tan(fovY/2), 1/aspect) — metres → UV per unit of w
uniform vec2  uAoNF;          // (1/near, 1/near - 1/far) — turns raw depth into w = 1/z

${TONEMAP_GLSL}
${SRGB_GLSL}

float lum709(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

/**
 * The sky dome's own gradient, evaluated for an arbitrary view ray.
 *
 * Deliberately the SAME expression as render/sky.js's fragment shader, minus the clouds and
 * the sun disc: a distant surface has to converge onto the sky it is seen against, and if
 * the two disagree the horizon grows a seam. Clouds are left out on purpose — a mountain
 * should read against the mean sky, not flicker as a cloud drifts behind it.
 */
vec3 skyAt(vec3 d) {
  float y = d.y;
  float g = pow(clamp(y, 0.0, 1.0), uSkyK.x);
  vec3 s = mix(uSkyHorizon, uSkyMid, clamp(g * 2.0, 0.0, 1.0));
  s = mix(s, uSkyZenith, clamp((g - 0.5) * 2.0, 0.0, 1.0));
  float hz = 1.0 - smoothstep(0.0, uSkyK.y, max(y, 0.0));
  s = mix(s, uSkyHaze, hz * 0.92);
  s = mix(s, uSkyHorizon, smoothstep(0.0, -0.06, y));   // below the horizon, hold the band
  return s * uSkyK.z;
}

/* ── sky occlusion ─────────────────────────────────────────────────────────────────
 *
 * The 0.78 rung of the reference value ladder (see tuning.js post.ao for the full
 * argument). Depth CURVATURE, not depth difference: for a perspective projection the stored
 * depth is affine in 1/z, so w = 1/z is exactly linear in screen space across ANY plane at
 * ANY angle. The second difference of w is therefore identically zero on a receding road, a
 * raking facade or a ramp, and non-zero only where the surface genuinely bends or breaks.
 * That is what stops this from being the usual depth-difference SSAO that paints a grey wash
 * over every ground plane it can see.
 */
float aoW(vec2 uv) { return uAoNF.x - texture2D(tDepth, uv).x * uAoNF.y; }

/**
 * One opposed tap PAIR. Returns how recessed the centre is relative to the two neighbours.
 *
 * Out-of-range taps are folded back onto the centre so the pair contributes exactly zero: a
 * silhouette against a far wall would otherwise report an enormous cavity and stamp a black
 * ring round every pole. The range test is done ENTIRELY IN w-SPACE —
 *   |1/w - 1/w0| > 3R   <=>   |w0 - w| > 3R * w * w0
 * — which is the same test without the two reciprocals. Sixteen divisions per pixel at
 * 1920x1080 is 33 M divisions a frame, and the perf gate is p99.
 */
float aoPair(vec2 uv, vec2 off, float w0, float rw) {
  float wp = aoW(uv + off);
  float wm = aoW(uv - off);
  float lp = rw * max(wp * w0, 1e-9);
  float lm = rw * max(wm * w0, 1e-9);
  wp = mix(w0, wp, 1.0 - smoothstep(lp * 0.4, lp, abs(wp - w0)));
  wm = mix(w0, wm, 1.0 - smoothstep(lm * 0.4, lm, abs(wm - w0)));
  return max(0.0, ((wp + wm) * 0.5 - w0) / w0);
}

float skyOcclusion(vec2 uv, float lum) {
  if (uAoStrength < 0.001) return 1.0;
  float d0 = texture2D(tDepth, uv).x;
  if (d0 > 0.99995) return 1.0;                       // sky is not occluded by anything
  float w0 = uAoNF.x - d0 * uAoNF.y;

  // Two world-space scales: tight creases (kerb lines, window reveals, the seam where a
  // staircase meets a wall) and broad cavities (doorways, under a truck, inside a ruelle).
  // Screen radius scaled by w so the WORLD radius is constant with distance. Clamped so a
  // cornice 90 m out still samples a real texel and a kerb 40 cm from the lens does not
  // sample a third of the frame; the near ring's clamp is tighter so the two scales stay
  // distinct in the near field instead of collapsing onto the same clamped value.
  float rvN = clamp(uAoRadius.x * w0 * uAoProj.x, uAoK.z, uAoK.w * 0.33);
  float rvF = clamp(uAoRadius.y * w0 * uAoProj.x, uAoK.z, uAoK.w);
  vec2 sN = vec2(rvN * uAoProj.y, rvN);
  vec2 sF = vec2(rvF * uAoProj.y, rvF);
  float rwN = uAoRadius.x * 3.0;
  float rwF = uAoRadius.y * 3.0;

  // Eight directions per ring counting the opposite taps, the far ring rotated 22.5 deg, so
  // the sixteen taps land on a 16-gon. Three directions was tried first and stamped a visible
  // HEXAGON round every loose chunk of asphalt. Unrolled with literal direction vectors: a
  // loop over cos(rot + k*step) costs eight transcendental pairs per pixel for eight
  // constants the compiler cannot fold through the ring ternary.
  const vec2 D0 = vec2( 1.00000,  0.00000);
  const vec2 D1 = vec2( 0.70711,  0.70711);
  const vec2 D2 = vec2( 0.00000,  1.00000);
  const vec2 D3 = vec2(-0.70711,  0.70711);
  const vec2 E0 = vec2( 0.92388,  0.38268);
  const vec2 E1 = vec2( 0.38268,  0.92388);
  const vec2 E2 = vec2(-0.38268,  0.92388);
  const vec2 E3 = vec2(-0.92388,  0.38268);

  float occ =
      aoPair(uv, D0 * sN, w0, rwN) + aoPair(uv, D1 * sN, w0, rwN)
    + aoPair(uv, D2 * sN, w0, rwN) + aoPair(uv, D3 * sN, w0, rwN)
    + aoPair(uv, E0 * sF, w0, rwF) + aoPair(uv, E1 * sF, w0, rwF)
    + aoPair(uv, E2 * sF, w0, rwF) + aoPair(uv, E3 * sF, w0, rwF);
  occ *= 0.125;

  float ao = 1.0 - uAoStrength * clamp(pow(occ * uAoK.x, uAoK.y), 0.0, 1.0);
  // A light SOURCE is not shaded by its own surroundings. Without this, the brightest core
  // of every sign, lamp and headlight gets a dirty rim and the whole thing reads as grime.
  return mix(ao, 1.0, smoothstep(uAoLightGuard, uAoLightGuard * 4.0, lum));
}

/**
 * Wet-road reflection — READ, not marched. The march happens in STREAK_FRAG at half
 * resolution; all that is left here is the part that genuinely needs full-res information.
 *
 *   · THE LATERAL BLUR. Three taps across the streak buffer's own texel is ~6 full-res
 *     pixels of cross-axis blur against a ~240 px along-axis smear — the 8-20x anisotropy
 *     reference §9.3 describes, and the reason the frame no longer carries a comb stipple.
 *   · THE ALBEDO BREAK. A crosswalk bar, a lane line or a fresh patch is a DIFFERENT
 *     SURFACE, and it stops the column dead. That edge only exists at full resolution, so
 *     the receiver gate has to run here.
 *   · THE 3-4x NORMALISATION. Criterion 5 is a ratio against the road BESIDE the streak, so
 *     it can only be solved against the receiver this pixel actually is.
 */
vec3 wetStreak(vec2 uv, float recvLum) {
  vec3 st = texture2D(tStreak, uv).rgb * 0.50
          + texture2D(tStreak, uv + vec2(uStreakTexel.x * 1.3, 0.0)).rgb * 0.25
          + texture2D(tStreak, uv - vec2(uStreakTexel.x * 1.3, 0.0)).rgb * 0.25;
  float sl = lum709(st);
  if (sl < 1e-6) return vec3(0.0);

  // Only DARK receivers take a reflection, and the ramp is narrow so the break has an edge.
  float gate = 1.0 - smoothstep(uStreakGate, uStreakGate * 3.0, recvLum);
  if (gate < 0.002) return vec3(0.0);

  // ── LAND EVERY REFLECTION IN THE 3-4x BAND, NOT JUST THE BRIGHTEST ────────────────
  // Round 8 measured three reflections in ONE frame at 3.26x, 2.22x and 1.37x against the
  // road beside them, where criterion 5 wants all of them at 3-4x — and separately measured
  // the hottest at 4.77x. Those two findings pull in opposite directions, so scaling the
  // whole term is the wrong operator: it makes the weak ones weaker. What it needs is
  // COMPRESSION toward the band. The streak is renormalised to a target multiple of the
  // receiver it actually lands on, then mixed back toward its own natural level so a tail
  // light still reads hotter than a shop window without running to 9x.
  //
  // ⚠ THE OLD OPERATOR WAS A NO-OP AND THE MEASUREMENT PROVED IT. It was
  //       st *= mix(1.0, clamp(want / sl, 0.15, 8.0), uStreakCompress)
  // — an ARITHMETIC blend between "leave it alone" and "hit the target". Every emitter in
  // this scene is HDR: a tail light sits near linear 1.0 while the wet road it reflects in
  // sits near 0.03, so want / sl is around 0.1 and pins to the 0.15 clamp, AND the 12%
  // that stays uncompressed is 0.12 x sl, which on its own is already four times the road.
  // Both surviving terms are proportional to sl, so uStreakRatio cancelled out entirely:
  // raising it from 2.30 to 2.90 to 4.30 moved the measured tail-light column 1.73 -> 2.09
  // -> 2.10. It was tuning a knob that was not connected.
  //
  // GEOMETRIC interpolation is the operator this actually wanted:
  //       result = want^compress * sl^(1-compress)
  // which lands on the target when compress = 1, leaves the source alone when it is 0, and
  // at 0.88 lets a source three times hotter come out only 14% hotter — the "a tail light
  // still reads hotter than a shop window" behaviour, now without the level running away.
  float want = max(recvLum, 1e-5) * uStreakRatio;
  float target = pow(want, uStreakCompress) * pow(max(sl, 1e-6), 1.0 - uStreakCompress);
  st *= clamp(target / sl, 0.0, 8.0);
  return st * (uStreak * gate);
}

void main() {
  vec3 hdr = texture2D(tScene, vUv).rgb;
  // Occlude the SURFACE, then add the things that are not surface: a reflection lying on a
  // wet road and a bloom halo are light arriving at the lens, not albedo, and shading them
  // would put a dark ring round every lamp.
  hdr *= skyOcclusion(vUv, lum709(hdr));

  // ── aerial perspective ───────────────────────────────────────────────────────────
  // The far end of the fog ramp, finished against the sky in THIS pixel's direction rather
  // than against one flat colour. See tuning.js fog.aerialNear for the measurement that
  // forced it. Runs before the reflection and the bloom, because both of those are light
  // arriving at the lens rather than surface, and haze does not sit in front of them.
  if (uAerial.z > 0.0) {
    // The sky dome writes NO depth (gl_Position.z = w, depthWrite off), so a sky pixel holds
    // the CLEAR value of exactly 1.0. Testing at 0.99995 threw away everything past ~1 km of
    // real geometry, which on a 1200 m far plane is most of the skyline.
    float dA = texture2D(tDepth, vUv).x;
    if (dA < 0.9999995) {
      vec4 clipA = uInvViewProj * vec4(vUv * 2.0 - 1.0, dA * 2.0 - 1.0, 1.0);
      vec3 rayA = clipA.xyz / clipA.w - uCamPos;
      float distA = length(rayA);
      float fA = smoothstep(uAerial.x, uAerial.y, distA);
      hdr = mix(hdr, skyAt(rayA / max(distA, 1e-4)) * uAerial.z, fA);
    }
  }

  if (uStreak > 0.001) hdr += wetStreak(vUv, lum709(hdr));
  hdr += texture2D(tBloom, vUv).rgb * uBloomStrength;
  hdr *= uExposure;

  vec3 c = pbrNeutral(hdr);
  c = linearToSRGB(c);

  // ── grade, in perceptual space ───────────────────────────────────────────────────
  // Saturation about luma. This is where the arcade chroma comes from; the palette is
  // authored honest and the grade adds the confidence.
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(l), c, uSaturation);

  // Gentle S-curve about a per-weather pivot. Daylight pivots at mid-grey; a night frame
  // whose own mid-point is near 0.18 must pivot there or the curve crushes the whole image.
  c = (c - uContrastPivot) * uContrast + uContrastPivot;

  // BLACK FLOOR. Cubic falloff: full effect at 0, one eighth at 0.5, nothing at 1. The floor
  // is a COLOUR, so a warm night can never clip its blue channel to zero.
  vec3 toe = vec3(1.0) - clamp(c, 0.0, 1.0);
  c += uShadowFloor * toe * toe * toe;

  c *= uTint;

  // ── vignette ─────────────────────────────────────────────────────────────────────
  vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
  float r = length(p) / (0.5 * uAspect);
  float v = 1.0 - uVignette * smoothstep(uVignetteRadius, uVignetteRadius + uVignetteSoftness, r);
  c *= v;

  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}
`;

/**
 * FXAA, run on the graded sRGB image. Perceptual space is where FXAA's luma test is
 * calibrated, so this has to be the last pass, after tonemapping and after the grade.
 * A compact edge-directed variant: 5 taps to classify the edge, 4 to resolve it.
 *
 * ⚠⚠ EVERYTHING BETWEEN THE NEXT BACKTICK AND ITS CLOSER IS GLSL, INCLUDING PROSE. ⚠⚠
 *
 * This warning lives HERE, in a JS comment, and not inside the string, because putting it
 * inside the string is how it broke the second time. Wave 8 broke the whole repo twice on
 * one line of documentation:
 *
 *   1. A markdown-style backtick pair around a symbol name CLOSED and REOPENED the tagged
 *      literal mid-shader. Every `npx vite build` in the tree failed with
 *      "src/render/post.js (547:46): Expected a semicolon" for 20+ minutes, and every other
 *      agent's `tools/verify.mjs` reported "build FAILED" against code that was not theirs.
 *   2. The warning written to prevent (1) was itself placed inside the shader string and
 *      used the word "template". GLSL RESERVES IT. The bundle then built fine — which is
 *      why nobody caught it at build time — but `post.fxaa` failed VALIDATE_STATUS at
 *      runtime with "'template' : Illegal use of reserved word", the final blit produced
 *      nothing, and every captured frame was a flat field with only the DOM HUD on top.
 *      `verify.mjs`'s blank-frame detector does not catch that: the HUD alone gives the
 *      histogram enough spread to pass.
 *
 * So: no backticks and no reserved words in shader-string comments. GLSL ES reserves far
 * more than it uses — `template`, `sample`, `filter`, `resource`, `typedef`, `namespace`,
 * `using`, `this`, `class`, `public`, `union`, `enum`, `static`, `long`, `short`, `half`,
 * `input`, `output`, `interface`, `sizeof`, `cast`, `partition`, `active`, `goto`,
 * `volatile`, `external`, `inline` — and it rejects them anywhere, comments included.
 * Block comments also do not nest, so a nested opener or closer terminates the comment
 * early and spills prose into the shader body.
 *
 * A shader that fails to compile is invisible to the bundler and nearly invisible to the
 * capture gate. Keep prose in JS comments, out here, where JS rules apply.
 */
const FXAA_FRAG = /* glsl */`
precision highp float;
#define MB_TAPS ${L.post.motionBlur.taps}
varying vec2 vUv;
uniform sampler2D tSrc;
uniform vec2 uTexel;
uniform float uEdgeThreshold;
uniform float uEdgeThresholdMin;
uniform float uSpanMax;

/* ── camera motion blur (see tuning.js post.motionBlur) ───────────────────────────── */
uniform sampler2D tDepth;
uniform vec3  uMbVel;      // camera velocity in VIEW space, m/s
uniform vec4  uMbK;        // (shutter*strength, maxLenUV, nearHold, nearFade)
uniform vec2  uMbProj;     // (P/aspect, P) where P = 1/tan(fovY/2)
uniform vec2  uMbNF;       // (1/near, 1/near - 1/far) — stored depth → w = 1/z

float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

/* Screen-space velocity of a STATIC world point, in UV per second, from the analytic
   derivative of the perspective divide. Deterministic: every term is a uniform or the
   depth buffer, and the velocity comes from ctx.player, which is sim state, sec 7.3.
   Returns the blur VECTOR, already scaled by shutter and clamped, or zero.
   Rationale and the authoring rules for this string live in the JS comment above it. */
vec2 motionVector(vec2 uv) {
  if (uMbK.x <= 0.0) return vec2(0.0);
  float d = texture2D(tDepth, uv).x;
  if (d > 0.9999995) return vec2(0.0);          // sky: no parallax, and it must stay clean
  float w = uMbNF.x - d * uMbNF.y;              // w = 1/z
  float z = 1.0 / max(w, 1e-6);
  // Ramp in past the hero. See tuning.js for why this is a depth cut and not a mask.
  float hold = smoothstep(uMbK.z, uMbK.z + uMbK.w, z);
  if (hold <= 0.0) return vec2(0.0);
  vec2 ndc = uv * 2.0 - 1.0;
  vec2 vNdc = -(vec2(uMbProj.x * uMbVel.x, uMbProj.y * uMbVel.y) + ndc * uMbVel.z) / z;
  vec2 v = vNdc * 0.5 * uMbK.x * hold;          // ndc → uv, then × shutter
  float len = length(v);
  if (len > uMbK.y) v *= uMbK.y / len;
  return v;
}

void main() {
  vec3 rgbM = texture2D(tSrc, vUv).rgb;

  // ── camera motion blur ────────────────────────────────────────────────────────────
  // A streak longer than a pixel IS the anti-aliasing along that axis, so blur and FXAA
  // are exclusive rather than stacked; running both would only cost taps. Below the
  // threshold the streak is sub-pixel and the two paths agree to within a LSB, so the
  // branch cannot show up as a visible boundary.
  vec2 mv = motionVector(vUv);
  if (length(mv) > uTexel.x * 0.75) {
    vec3 acc = rgbM;
    for (int i = 1; i <= MB_TAPS; i++) {
      float t = float(i) / float(MB_TAPS);
      acc += texture2D(tSrc, vUv + mv * t).rgb;
      acc += texture2D(tSrc, vUv - mv * t).rgb;
    }
    gl_FragColor = vec4(acc / float(2 * MB_TAPS + 1), 1.0);
    return;
  }

  vec3 rgbNW = texture2D(tSrc, vUv + vec2(-1.0, -1.0) * uTexel).rgb;
  vec3 rgbNE = texture2D(tSrc, vUv + vec2( 1.0, -1.0) * uTexel).rgb;
  vec3 rgbSW = texture2D(tSrc, vUv + vec2(-1.0,  1.0) * uTexel).rgb;
  vec3 rgbSE = texture2D(tSrc, vUv + vec2( 1.0,  1.0) * uTexel).rgb;

  float lM = luma(rgbM), lNW = luma(rgbNW), lNE = luma(rgbNE), lSW = luma(rgbSW), lSE = luma(rgbSE);
  float lMin = min(lM, min(min(lNW, lNE), min(lSW, lSE)));
  float lMax = max(lM, max(max(lNW, lNE), max(lSW, lSE)));
  float range = lMax - lMin;

  // Flat area — leave it alone. This is what stops FXAA smearing texture detail.
  if (range < max(uEdgeThresholdMin, lMax * uEdgeThreshold)) {
    gl_FragColor = vec4(rgbM, 1.0);
    return;
  }

  vec2 dir = vec2(
    -((lNW + lNE) - (lSW + lSE)),
     ((lNW + lSW) - (lNE + lSE))
  );
  float dirReduce = max((lNW + lNE + lSW + lSE) * 0.25 * 0.03125, 0.0078125);
  float rcpDirMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
  dir = clamp(dir * rcpDirMin, vec2(-uSpanMax), vec2(uSpanMax)) * uTexel;

  vec3 rgbA = 0.5 * (
    texture2D(tSrc, vUv + dir * (1.0 / 3.0 - 0.5)).rgb +
    texture2D(tSrc, vUv + dir * (2.0 / 3.0 - 0.5)).rgb);
  vec3 rgbB = rgbA * 0.5 + 0.25 * (
    texture2D(tSrc, vUv + dir * -0.5).rgb +
    texture2D(tSrc, vUv + dir *  0.5).rgb);

  float lB = luma(rgbB);
  gl_FragColor = vec4((lB < lMin || lB > lMax) ? rgbA : rgbB, 1.0);
}
`;

/* ── full-screen triangle ─────────────────────────────────────────────────────────── */

function makeTriangle() {
  const g = new THREE.BufferGeometry();
  // One oversized triangle beats two triangles: no diagonal seam, one fewer vertex.
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));
  return g;
}

/* ══════════════════════════════════════════════════════════════════════════════════════
   PostStack
   ══════════════════════════════════════════════════════════════════════════════════════ */

export class PostStack {
  constructor(renderer, quality = 'high', samplesOverride = null) {
    this.renderer = renderer;
    this.enabled = true;
    this.width = 2;
    this.height = 2;

    const samples = samplesOverride != null && isFinite(samplesOverride)
      ? Math.max(0, samplesOverride | 0)
      : (L.post.samples[quality] != null ? L.post.samples[quality] : L.post.samples.high);
    this.samples = samples;

    // HalfFloat so bloom has real HDR headroom above 1.0. Fall back to 8-bit if the
    // context cannot render to it — bloom still works, just with a clipped source.
    const halfFloatOk = renderer.capabilities.isWebGL2
      || renderer.extensions.has('EXT_color_buffer_half_float');
    this.hdrType = halfFloatOk ? THREE.HalfFloatType : THREE.UnsignedByteType;

    this.rtScene = new THREE.WebGLRenderTarget(2, 2, {
      type: this.hdrType,
      format: THREE.RGBAFormat,
      colorSpace: THREE.LinearSRGBColorSpace,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
      samples,
    });
    this.rtScene.texture.name = 'post.scene';

    // Depth, so the composite can tell wet road from bodywork. See `groundMask` above.
    // Nearest filtering: an interpolated depth is a depth of nothing.
    this.depthTexture = new THREE.DepthTexture(2, 2);
    this.depthTexture.type = THREE.UnsignedIntType;
    this.depthTexture.format = THREE.DepthFormat;
    this.depthTexture.minFilter = THREE.NearestFilter;
    this.depthTexture.magFilter = THREE.NearestFilter;
    this.depthTexture.name = 'post.depth';
    this.rtScene.depthTexture = this.depthTexture;

    const bloomOpts = {
      type: this.hdrType,
      format: THREE.RGBAFormat,
      colorSpace: THREE.LinearSRGBColorSpace,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    };
    this.rtBloomA = new THREE.WebGLRenderTarget(2, 2, bloomOpts);
    this.rtBloomB = new THREE.WebGLRenderTarget(2, 2, bloomOpts);
    this.rtBloomA.texture.name = 'post.bloomA';
    this.rtBloomB.texture.name = 'post.bloomB';

    // The graded LDR image FXAA reads. 8-bit and tagged LINEAR on purpose: the pixels are
    // already sRGB-encoded by the composite pass, so the sampler must not decode them again.
    this.fxaaEnabled = L.post.fxaa;
    this.rtLDR = new THREE.WebGLRenderTarget(2, 2, {
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      colorSpace: THREE.LinearSRGBColorSpace,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.rtLDR.texture.name = 'post.ldr';

    // The wet-road reflection, at 1/`wetStreakDownscale` resolution. Half-float because a
    // reflection is HDR light — a streak off a lamp core legitimately sits above 1.0 and the
    // composite's normalisation needs the headroom (§4.8b). LinearFilter is load-bearing:
    // the bilinear upsample in the composite is the cross-axis blur.
    this.rtStreak = new THREE.WebGLRenderTarget(2, 2, bloomOpts);
    this.rtStreak.texture.name = 'post.streak';
    this._streakLive = false;

    this._geo = makeTriangle();
    this._cam = new THREE.Camera();
    this._scene = new THREE.Scene();
    this._scene.matrixAutoUpdate = false;
    this._quad = new THREE.Mesh(this._geo, null);
    this._quad.frustumCulled = false;
    this._quad.matrixAutoUpdate = false;
    this._scene.add(this._quad);

    this.matBright = new THREE.ShaderMaterial({
      name: 'post.bright',
      uniforms: {
        tScene: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uThreshold: { value: L.post.bloomThreshold.thaw },
        uSoftKnee: { value: L.post.bloomSoftKnee },
        uExposure: { value: L.renderer.exposure },
      },
      vertexShader: VERT, fragmentShader: BRIGHT_FRAG,
      depthTest: false, depthWrite: false, toneMapped: false,
    });

    this.matBlur = new THREE.ShaderMaterial({
      name: 'post.blur',
      uniforms: { tSrc: { value: null }, uDir: { value: new THREE.Vector2() } },
      vertexShader: VERT, fragmentShader: BLUR_FRAG,
      depthTest: false, depthWrite: false, toneMapped: false,
    });

    this.matStreak = new THREE.ShaderMaterial({
      name: 'post.streak',
      uniforms: {
        tScene: { value: this.rtScene.texture },
        tBloom: { value: this.rtBloomA.texture },
        tDepth: { value: this.depthTexture },
        uInvViewProj: { value: new THREE.Matrix4() },
        uCamPos: { value: new THREE.Vector3() },
        uCamRight: { value: new THREE.Vector3(1, 0, 0) },
        uGroundY: { value: 0 },
        uStreakFar: { value: L.post.wetStreakFar },
        uHorizonV: { value: 0.62 },
        uStreakLen: { value: L.post.wetStreakLength },
        uStreakSrcMin: { value: L.post.wetStreakSrcMin },
        uBreakDepth: { value: L.post.wetStreakBreakDepth },
        uBreakScale: { value: L.post.wetStreakBreakScale },
        uCamber: { value: L.post.wetStreakCamber },
        uChroma: { value: L.post.wetStreakChroma },
        uTexel: { value: new THREE.Vector2() },
      },
      vertexShader: VERT, fragmentShader: STREAK_FRAG,
      depthTest: false, depthWrite: false, toneMapped: false,
    });

    this.matComposite = new THREE.ShaderMaterial({
      name: 'post.composite',
      uniforms: {
        tScene: { value: this.rtScene.texture },
        tBloom: { value: this.rtBloomA.texture },
        tStreak: { value: this.rtStreak.texture },
        uStreakTexel: { value: new THREE.Vector2() },
        uBloomStrength: { value: L.post.bloomStrength.thaw },
        uExposure: { value: L.renderer.exposure },
        uSaturation: { value: L.post.saturation.thaw },
        uContrast: { value: L.post.contrast.thaw },
        uContrastPivot: { value: L.post.contrastPivot.thaw },
        uShadowFloor: { value: new THREE.Vector3(6 / 255, 6 / 255, 7 / 255) },
        uTint: { value: new THREE.Vector3(1, 1, 1) },
        uVignette: { value: L.post.vignetteStrength.thaw },
        uVignetteRadius: { value: L.post.vignetteRadius },
        uVignetteSoftness: { value: L.post.vignetteSoftness },
        uAspect: { value: 16 / 9 },
        uStreak: { value: 0 },
        uStreakGate: { value: L.post.wetStreakGate },
        uStreakRatio: { value: L.post.wetStreakRatio },
        uStreakCompress: { value: L.post.wetStreakCompress },
        tDepth: { value: this.depthTexture },
        uInvViewProj: { value: new THREE.Matrix4() },
        uCamPos: { value: new THREE.Vector3() },
        uAerial: { value: new THREE.Vector3(L.fog.aerialNear.thaw, L.fog.aerialFar.thaw, L.fog.aerialLevel.thaw) },
        uSkyHorizon: { value: new THREE.Color(0x8DC3FC) },
        uSkyMid: { value: new THREE.Color(0x4582FB) },
        uSkyZenith: { value: new THREE.Color(0x2A62EE) },
        uSkyHaze: { value: new THREE.Color(0xC6E2F7) },
        uSkyK: { value: new THREE.Vector3(L.sky.gradientPower.thaw, L.sky.horizonSoft.thaw, L.sky.gain.thaw) },
        uAoStrength: { value: L.post.ao.strength.thaw },
        uAoRadius: { value: new THREE.Vector2(L.post.ao.radiusNear, L.post.ao.radiusFar) },
        uAoK: {
          value: new THREE.Vector4(
            L.post.ao.gain, L.post.ao.power, L.post.ao.minRadiusUV, L.post.ao.maxRadiusUV),
        },
        uAoLightGuard: { value: L.post.ao.lightGuard },
        uAoProj: { value: new THREE.Vector2(0.5 / Math.tan(30 * Math.PI / 180), 9 / 16) },
        uAoNF: { value: new THREE.Vector2(1 / 0.1, 1 / 0.1 - 1 / 1000) },
      },
      vertexShader: VERT, fragmentShader: COMPOSITE_FRAG,
      depthTest: false, depthWrite: false, toneMapped: false,
    });

    this.matFxaa = new THREE.ShaderMaterial({
      name: 'post.fxaa',
      uniforms: {
        tSrc: { value: this.rtLDR.texture },
        uTexel: { value: new THREE.Vector2() },
        uEdgeThreshold: { value: L.post.fxaaEdgeThreshold },
        uEdgeThresholdMin: { value: L.post.fxaaEdgeThresholdMin },
        uSpanMax: { value: L.post.fxaaSpanMax },
        tDepth: { value: this.depthTexture },
        uMbVel: { value: new THREE.Vector3() },
        uMbK: { value: new THREE.Vector4(0, L.post.motionBlur.maxLenUV,
          L.post.motionBlur.nearHold, L.post.motionBlur.nearFade) },
        uMbProj: { value: new THREE.Vector2(1 / Math.tan(30 * Math.PI / 180) * 9 / 16,
          1 / Math.tan(30 * Math.PI / 180)) },
        uMbNF: { value: new THREE.Vector2(1 / 0.1, 1 / 0.1 - 1 / 1000) },
      },
      vertexShader: VERT, fragmentShader: FXAA_FRAG,
      depthTest: false, depthWrite: false, toneMapped: false,
    });
  }

  setSize(width, height) {
    const dpr = this.renderer.getPixelRatio();
    const w = Math.max(2, Math.floor(width * dpr));
    const h = Math.max(2, Math.floor(height * dpr));
    if (w === this.width && h === this.height) return;
    this.width = w; this.height = h;

    this.rtScene.setSize(w, h);
    const d = L.post.bloomDownscale;
    const bw = Math.max(2, Math.floor(w / d));
    const bh = Math.max(2, Math.floor(h / d));
    this.rtBloomA.setSize(bw, bh);
    this.rtBloomB.setSize(bw, bh);
    this.rtLDR.setSize(w, h);

    const sd = L.post.wetStreakDownscale;
    const sw = Math.max(2, Math.floor(w / sd));
    const sh = Math.max(2, Math.floor(h / sd));
    this.rtStreak.setSize(sw, sh);
    this.matStreak.uniforms.uTexel.value.set(1 / sw, 1 / sh);
    this.matComposite.uniforms.uStreakTexel.value.set(1 / sw, 1 / sh);

    this.matBright.uniforms.uTexel.value.set(1 / w, 1 / h);
    this.matComposite.uniforms.uAspect.value = w / h;
    this.matFxaa.uniforms.uTexel.value.set(1 / w, 1 / h);
  }

  /** Push the current weather profile into the post uniforms. */
  applyProfile(p, exposure) {
    this.matBright.uniforms.uThreshold.value = p.bloomThreshold;
    this.matBright.uniforms.uExposure.value = exposure;
    const c = this.matComposite.uniforms;
    c.uBloomStrength.value = p.bloomStrength;
    c.uExposure.value = exposure;
    c.uSaturation.value = p.saturation;
    c.uContrast.value = p.contrast;
    c.uContrastPivot.value = p.contrastPivot;
    c.uShadowFloor.value.copy(p.shadowFloor);
    c.uTint.value.copy(p.tint);
    c.uVignette.value = p.vignette;
    c.uStreak.value = p.wetStreak;
    c.uAoStrength.value = p.ao;
    // Aerial perspective needs the same sky the dome is drawing, blended exactly as the
    // weather cross-fade blended it — otherwise a transition puts a seam on the horizon.
    c.uAerial.value.set(p.aerialNear, p.aerialFar, p.aerialLevel);
    c.uSkyHorizon.value.copy(p.horizon);
    c.uSkyMid.value.copy(p.mid);
    c.uSkyZenith.value.copy(p.zenith);
    c.uSkyHaze.value.copy(p.haze);
    c.uSkyK.value.set(p.gradientPower, p.horizonSoft, p.skyGain);
  }

  /**
   * Screen v of the horizon, so the wet-road reflection never smears the sky onto a wall.
   * `look` computes it from the live camera each frame; see LookSystem.update().
   */
  setHorizon(v) { this.matStreak.uniforms.uHorizonV.value = v; }

  /**
   * Camera state for the two effects that read the depth buffer — sky occlusion and the
   * wet-road reflection's ground test. Called once per frame by `look` from `update()`, i.e.
   * before the render: the camera matrices are already current there because `vehicle` drives
   * the chase rig earlier in the frame order (§2 step 4). Allocation-free.
   */
  setCamera(camera) {
    const u = this.matComposite.uniforms;
    const s = this.matStreak.uniforms;
    s.uInvViewProj.value.multiplyMatrices(camera.matrixWorld, camera.projectionMatrixInverse);
    s.uCamPos.value.copy(camera.position);
    u.uInvViewProj.value.copy(s.uInvViewProj.value);
    u.uCamPos.value.copy(camera.position);
    // The camera's own RIGHT vector, straight out of the world matrix's first column. The
    // reflection leans with the road's camber, and "how far does this surface tilt ACROSS
    // the view" is exactly dot(surfaceNormal, cameraRight).
    s.uCamRight.value.set(camera.matrixWorld.elements[0],
      camera.matrixWorld.elements[1], camera.matrixWorld.elements[2]).normalize();
    // Metres → UV per unit of w, and the affine map from stored depth to w = 1/z.
    // Both are pure functions of the projection, so they must track a camera whose fov or
    // clip planes a shot has changed (`harness.camera({fov})` does exactly that).
    if (camera.isPerspectiveCamera) {
      const tanHalf = Math.tan(camera.fov * 0.5 * Math.PI / 180);
      u.uAoProj.value.set(0.5 / tanHalf, this.height / this.width);
      u.uAoNF.value.set(1 / camera.near, 1 / camera.near - 1 / camera.far);
      // Motion blur reads the same projection, but as the FULL ndc scale rather than the
      // metres→UV-per-w form AO wants: ndc.x = (P/aspect)·x/z, ndc.y = P·y/z.
      const f = this.matFxaa.uniforms;
      f.uMbProj.value.set((1 / tanHalf) * (this.height / this.width), 1 / tanHalf);
      f.uMbNF.value.copy(u.uAoNF.value);
    }
  }

  /**
   * Camera translation for the motion blur, already resolved into the VIEW basis by `look`.
   * `strength` folds shutter × speed ramp × weather scale into one number; 0 disables the
   * whole path with a uniform branch.
   */
  setMotion(vx, vy, vz, strength) {
    const f = this.matFxaa.uniforms;
    f.uMbVel.value.set(vx, vy, vz);
    f.uMbK.value.x = strength;
  }

  /** Height of the carriageway, for the wet-road reflection's ground test. */
  setGroundY(groundY) { this.matStreak.uniforms.uGroundY.value = groundY; }

  /** Render target the scene must be drawn into. */
  get sceneTarget() { return this.rtScene; }

  _blit(material, target) {
    this._quad.material = material;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this._scene, this._cam);
  }

  /**
   * Run bloom + composite. The scene must already have been rendered into `rtScene`.
   * Leaves the renderer bound to the canvas.
   */
  run() {
    const r = this.renderer;

    // Bright-pass + downsample.
    this.matBright.uniforms.tScene.value = this.rtScene.texture;
    this._blit(this.matBright, this.rtBloomA);

    // Separable blur, ping-pong.
    const bw = this.rtBloomA.width, bh = this.rtBloomA.height;
    const rad = L.post.bloomRadius;
    for (let i = 0; i < L.post.bloomIterations; i++) {
      const spread = rad * (1 + i * 0.75);
      this.matBlur.uniforms.tSrc.value = this.rtBloomA.texture;
      this.matBlur.uniforms.uDir.value.set(spread / bw, 0);
      this._blit(this.matBlur, this.rtBloomB);

      this.matBlur.uniforms.tSrc.value = this.rtBloomB.texture;
      this.matBlur.uniforms.uDir.value.set(0, spread / bh);
      this._blit(this.matBlur, this.rtBloomA);
    }

    // Wet-road reflection, into its own half-res buffer. Skipped entirely when dry — the
    // two daylight weathers pay nothing for this, not even the bandwidth of clearing it.
    const wet = this.matComposite.uniforms.uStreak.value > 0.001;
    if (wet) {
      this.matStreak.uniforms.tScene.value = this.rtScene.texture;
      this.matStreak.uniforms.tBloom.value = this.rtBloomA.texture;
      this._blit(this.matStreak, this.rtStreak);
      this._streakLive = true;
    } else if (this._streakLive) {
      // One clearing blit the first dry frame after a wet one, so a stale reflection cannot
      // survive a weather change. After that the flag keeps us out of here.
      this.matStreak.uniforms.tScene.value = this.rtScene.texture;
      this.matStreak.uniforms.tBloom.value = this.rtBloomA.texture;
      r.setRenderTarget(this.rtStreak);
      r.clear(true, false, false);
      this._streakLive = false;
    }

    // Tonemap + grade, then edge AA on the graded result.
    this.matComposite.uniforms.tScene.value = this.rtScene.texture;
    this.matComposite.uniforms.tBloom.value = this.rtBloomA.texture;
    if (this.fxaaEnabled) {
      this._blit(this.matComposite, this.rtLDR);
      this.matFxaa.uniforms.tSrc.value = this.rtLDR.texture;
      this._blit(this.matFxaa, null);
    } else {
      this._blit(this.matComposite, null);
    }
    // NOTE: we deliberately do NOT unbind here — see LookSystem.render().
  }

  stats() {
    return {
      width: this.width, height: this.height,
      samples: this.samples,
      fxaa: this.fxaaEnabled,
      hdr: this.hdrType === THREE.HalfFloatType ? 'half' : 'byte',
      bloomWidth: this.rtBloomA.width, bloomHeight: this.rtBloomA.height,
      streakWidth: this.rtStreak.width, streakHeight: this.rtStreak.height,
      streak: +this.matComposite.uniforms.uStreak.value.toFixed(3),
    };
  }

  dispose() {
    this.rtScene.dispose();
    this.rtBloomA.dispose();
    this.rtBloomB.dispose();
    this.rtStreak.dispose();
    this.rtLDR.dispose();
    this.depthTexture.dispose();
    this.matBright.dispose();
    this.matBlur.dispose();
    this.matStreak.dispose();
    this.matComposite.dispose();
    this.matFxaa.dispose();
    this._geo.dispose();
  }
}

export default PostStack;
